import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Hook to fetch SNS content from Firestore
 * Optionally triggers a refresh from the Cloud Function
 */
export const useSNSContent = (options = {}) => {
  const {
    type = null, // Filter by type: 'x', 'youtube', 'note', or null for all
    count = 5,
    autoRefresh = false, // Set to true to call the Cloud Function on mount
    approved = true, // Only show approved content by default
    prioritizeFeatured = false // Show featured content first, then fill with recent
  } = options;

  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Function to fetch content from Firestore
  const fetchContent = async () => {
    try {
      setLoading(true);
      const snsRef = collection(db, 'snsContent');
      let items = [];
      
      if (prioritizeFeatured) {
        try {
          // First, fetch featured content
          let featuredQuery;
          if (type) {
            featuredQuery = query(
              snsRef,
              where('type', '==', type),
              where('featured', '==', true),
              where('approved', '==', true),
              orderBy('date', 'desc'),
              limit(count)
            );
          } else {
            featuredQuery = query(
              snsRef,
              where('featured', '==', true),
              where('approved', '==', true),
              orderBy('date', 'desc'),
              limit(count)
            );
          }
          
          const featuredSnapshot = await getDocs(featuredQuery);
          const featuredItems = featuredSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            date: convertDate(doc.data().date)
          }));
          
          items = [...featuredItems];
          
          // If we need more items, fetch recent approved content
          if (items.length < count) {
            const remaining = count - items.length;
            let recentQuery;
            
            if (type) {
              recentQuery = query(
                snsRef,
                where('type', '==', type),
                where('approved', '==', true),
                orderBy('date', 'desc'),
                limit(remaining * 2)
              );
            } else {
              recentQuery = query(
                snsRef,
                where('approved', '==', true),
                orderBy('date', 'desc'),
                limit(remaining * 2)
              );
            }
            
            const recentSnapshot = await getDocs(recentQuery);
            const recentItems = recentSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              date: convertDate(doc.data().date)
            }));
            
            // Add recent items that aren't already featured
            const featuredIds = new Set(items.map(item => item.id));
            const additionalItems = recentItems
              .filter(item => !featuredIds.has(item.id))
              .slice(0, remaining);
            
            items = [...items, ...additionalItems];
          }
        } catch (featuredError) {
          // If featured query fails (e.g., index still building), fall back to original logic
          console.warn('Featured query failed, falling back to recent content:', featuredError.message);
          
          // Fall through to original logic below
          let q;
          if (type) {
            q = query(snsRef, where('type', '==', type), orderBy('date', 'desc'), limit(count * 2));
          } else {
            q = query(snsRef, orderBy('date', 'desc'), limit(count * 2));
          }
          
          const snapshot = await getDocs(q);
          items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            date: convertDate(doc.data().date)
          }));
          
          // Filter approved in code if needed
          if (approved) {
            items = items.filter(item => item.approved === true);
          }
          
          // Limit after filtering
          items = items.slice(0, count);
        }
      } else {
        // Original logic: fetch by date only
        let q;
        
        if (type) {
          q = query(snsRef, where('type', '==', type), orderBy('date', 'desc'), limit(count * 2));
        } else {
          q = query(snsRef, orderBy('date', 'desc'), limit(count * 2));
        }
        
        const snapshot = await getDocs(q);
        items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: convertDate(doc.data().date)
        }));
        
        // Filter approved in code if needed
        if (approved) {
          items = items.filter(item => item.approved === true);
        }
        
        // Limit after filtering
        items = items.slice(0, count);
      }
      
      setContent(items);
      setError(null);
    } catch (err) {
      console.error('Error fetching SNS content:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to trigger Cloud Function refresh
  const refreshFromAPIs = async () => {
    try {
      setRefreshing(true);
      // Use the HTTP endpoint instead of callable function to avoid CORS issues
      const response = await fetch('https://us-central1-recruit-flaretech.cloudfunctions.net/fetchSNSContentHTTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('SNS content refreshed:', result);
      
      // Re-fetch from Firestore after refresh
      await fetchContent();
      
      return result;
    } catch (err) {
      console.error('Error refreshing SNS content:', err);
      setError(err.message);
      throw err;
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const initContent = async () => {
      if (autoRefresh) {
        // Call Cloud Function first, then fetch
        try {
          await refreshFromAPIs();
        } catch (err) {
          // Even if refresh fails, try to get existing content
          await fetchContent();
        }
      } else {
        // Just fetch existing content
        await fetchContent();
      }
    };

    initContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, count, approved, prioritizeFeatured]);

  // Helper function to convert dates
  const convertDate = (dateField) => {
    if (!dateField) return null;
    if (typeof dateField.toDate === 'function') {
      return dateField.toDate();
    }
    if (dateField instanceof Date) {
      return Number.isNaN(dateField.getTime()) ? null : dateField;
    }
    if (typeof dateField.seconds === 'number') {
      return new Date(dateField.seconds * 1000);
    }
    if (typeof dateField === 'string' || typeof dateField === 'number') {
      const parsed = new Date(dateField);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  return {
    content,
    loading,
    error,
    refreshing,
    refresh: refreshFromAPIs,
    refetch: fetchContent
  };
};

/**
 * Hook to get content separated by type.
 * Public pages show newest approved items. Featured pinning is unused on the site.
 */
export const useSNSContentByType = (count = 5, autoRefresh = false, prioritizeFeatured = false) => {
  const { content: xPosts, loading: xLoading } = useSNSContent({ type: 'x', count, autoRefresh: false, prioritizeFeatured });
  const { content: youtubeVideos, loading: ytLoading } = useSNSContent({ type: 'youtube', count, autoRefresh, prioritizeFeatured });
  const { content: noteArticles, loading: noteLoading } = useSNSContent({ type: 'note', count, autoRefresh, prioritizeFeatured });
  
  return {
    xPosts,
    youtubeVideos,
    noteArticles,
    loading: xLoading || ytLoading || noteLoading
  };
};
